import {
  CapabilityGrants,
  ContextStream,
  InitMessage,
  PortMessage,
  RequestMessage,
  ResponseMessage,
  UiConfirmation,
  UiNotification,
  WIDGET_PROTOCOL,
  WidgetContext,
  WidgetErrorCode,
  DataDefinition,
  DataDocument,
  dataStream,
  handoffGrant,
} from './protocol';

/**
 * What a platform must implement to host custom widgets. Each handler group is
 * only invoked after the grant check passes, so implementations never need to
 * re-check capabilities. Everything is Promise-based; the host core owns the
 * request/response bookkeeping.
 */
export interface WidgetHostHandlers {
  data?: {
    get(topic: string): Promise<unknown>;
    /**
     * Returns a teardown; push() delivers every update for the topic. This is
     * also how a hand-off outcome reaches a widget that survived its hand-off:
     * the 'handoffs' topic pushes the updated record list like any other topic.
     */
    subscribe?(topic: string, push: (payload: unknown) => void): () => void;
  };
  api?: { call(operation: string, payload: Record<string, unknown>): Promise<unknown> };
  forms?: {
    get(path: string): Promise<unknown>;
    set(path: string, value: unknown): Promise<void>;
    validity(): Promise<boolean>;
  };
  nav?: { goToPage(pageId: string): Promise<void>; back(): Promise<void> };
  ui?: { notify(notification: UiNotification): Promise<void>; confirm(confirmation: UiConfirmation): Promise<boolean> };
  /**
   * Hand-offs. start() owns everything after the widget's declaration:
   * persisting the correlation for this widget instance, obtaining the
   * destination URL, and handing control over by whatever means this
   * environment provides — a browser redirect, a native shell's external-URL
   * bridge, a shell-owned webview. It resolves once the hand-off has been
   * accepted and begun.
   *
   * Two obligations follow from the host not knowing whether the widget
   * survives:
   *
   * - Persist the record *before* handing control over, because this document
   *   may never come back.
   * - When a record reaches a terminal status while the widget is still live,
   *   push the new 'handoffs' payload through the `data.subscribe` handler —
   *   the ordinary data-topic push channel, no special case in this core. A
   *   surviving widget then sees the outcome without a remount, which is the
   *   only thing that makes the "widget survives" path real.
   *
   * Which transport was used never reaches the widget: no environment field
   * appears in any protocol message or in any hand-off record served on the
   * 'handoffs' topic.
   */
  handoff?: { start(kind: string, params: Record<string, unknown>): Promise<{ handoffId: string }> };
  /**
   * The widget's per-instance scratchpad. Implementations MUST scope storage to
   * the instance in `context`: state.* carries no grant, so instance scoping is
   * the only thing keeping one widget's draft out of another's reach.
   */
  state?: { get(): Promise<unknown>; set(value: unknown): Promise<void> };
  storage?: {
    get(contract: string): Promise<DataDocument | undefined>;
    set(
      contract: string,
      value: Record<string, unknown>,
      options?: { ifRevision?: number },
    ): Promise<DataDocument>;
    remove(contract: string, options?: { ifRevision?: number }): Promise<void>;
    catalog(): Promise<DataDefinition[]>;
  };
  /**
   * Invokes a backend function by slug. The implementation attaches the caller's
   * session itself — the payload is the widget's, the identity never is — and
   * returns the function's own output verbatim.
   *
   * A host that serves no functions simply omits this group; the core then
   * answers a granted call with 'host-error' rather than pretending the function
   * ran. `unknown-operation` is reserved for a slug that is granted here but not
   * activated on the project, which only the implementation can know.
   */
  functions?: { invoke(slug: string, payload: unknown): Promise<unknown> };
  /** Widget-emitted custom events; always allowed, host decides what to do. */
  onEvent?(name: string, payload: unknown): void;
  /** Frame content height changes; drive the iframe element's height. */
  onResize?(height: number): void;
  /** Widget finished mounting. */
  onReady?(): void;
}

export interface WidgetHost {
  /**
   * Creates the MessageChannel, wires the host side, and returns the port to
   * deliver to the widget (transferred in the init message). initInto() does
   * both steps for a real window; tests hand the port to createWidgetClient.
   */
  openPort(): MessagePort;
  /** Performs the full handshake against a frame's contentWindow. */
  initInto(target: Window, targetOrigin: string): void;
  /** Pushes a context stream update (theme/settings/context) to the widget. */
  pushContext(stream: ContextStream, payload: unknown): void;
  readonly initMessage: InitMessage;
  dispose(): void;
}

/**
 * Error contract between host handlers and the protocol core: a handler that
 * throws a HostError surfaces its `code` to the widget verbatim (e.g.
 * 'confirmation-declined' when a confirmation-gated operation is dismissed);
 * any other thrown error is mapped to the generic 'host-error' code.
 *
 * `details` is the structured part the widget is expected to act on — the
 * retry-after seconds of a 'rate-limited' rejection, the outstanding steps of an
 * 'onboarding-prerequisite-unmet' one. It crosses to the bundle verbatim, so it
 * must carry nothing a widget may not hold: no tokens, no provider payloads, no
 * customer data beyond what the widget already sent.
 *
 * `serverCode` is the platform's own refusal slug (`pot-not-empty`, …) behind
 * the closed `code`; it reaches the widget as `WidgetOperationError.serverCode`.
 */
export class HostError extends Error {
  constructor(
    public readonly code: WidgetErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>,
    public readonly serverCode?: string,
  ) {
    super(message);
  }
}

function denied(what: string): never {
  throw new HostError('capability-denied', `capability not granted: ${what}`);
}

/**
 * Framework-neutral bridge core. The Angular host (and any future host)
 * supplies handlers; this owns the protocol, the grant enforcement, and the
 * subscription lifecycle.
 */
export function createWidgetHost(options: {
  context: WidgetContext;
  grants: CapabilityGrants;
  handlers: WidgetHostHandlers;
}): WidgetHost {
  const { context, grants, handlers } = options;
  let port: MessagePort | undefined;
  const subscriptions = new Map<string, () => void>();

  async function dispatch(request: RequestMessage): Promise<unknown> {
    const params = request.params;
    switch (request.method) {
      case 'data.get':
      case 'data.subscribe':
      case 'data.unsubscribe': {
        const topic = params['topic'] as string;
        if (!grants.data.includes(topic)) denied(`data:${topic}`);
        if (!handlers.data) throw new HostError('unknown-topic', `no data handler for ${topic}`);
        if (request.method === 'data.get') return handlers.data.get(topic);
        if (request.method === 'data.subscribe') {
          if (!subscriptions.has(topic) && handlers.data.subscribe) {
            subscriptions.set(
              topic,
              handlers.data.subscribe(topic, (payload) =>
                port?.postMessage({ type: 'stream', stream: dataStream(topic), payload }),
              ),
            );
          }
          return undefined;
        }
        subscriptions.get(topic)?.();
        subscriptions.delete(topic);
        return undefined;
      }
      case 'api.call': {
        const operation = params['operation'] as string;
        if (!grants.api.includes(operation)) denied(`api:${operation}`);
        if (!handlers.api) throw new HostError('unknown-operation', `no api handler for ${operation}`);
        return handlers.api.call(operation, (params['payload'] as Record<string, unknown>) ?? {});
      }
      case 'forms.get':
      case 'forms.set':
      case 'forms.validity': {
        if (!grants.forms) denied('forms');
        if (!handlers.forms) throw new HostError('host-error', 'forms are not available on this host');
        if (request.method === 'forms.get') return handlers.forms.get(params['path'] as string);
        if (request.method === 'forms.set') return handlers.forms.set(params['path'] as string, params['value']);
        return handlers.forms.validity();
      }
      case 'nav.goToPage':
      case 'nav.back': {
        if (!grants.nav) denied('nav');
        if (!handlers.nav) throw new HostError('host-error', 'navigation is not available on this host');
        return request.method === 'nav.goToPage'
          ? handlers.nav.goToPage(params['pageId'] as string)
          : handlers.nav.back();
      }
      case 'handoff.start': {
        const kind = params['kind'] as string;
        // Deny-by-default per kind: hand-offs are api grants under the
        // `handoff:` namespace, so one granted kind never authorises another
        // and a plain api grant never authorises a redirect.
        if (!grants.api.includes(handoffGrant(kind))) denied(handoffGrant(kind));
        if (!handlers.handoff) throw new HostError('host-error', 'hand-offs are not available on this host');
        return handlers.handoff.start(kind, (params['params'] as Record<string, unknown>) ?? {});
      }
      case 'state.get':
      case 'state.set': {
        // No grant: the scratchpad holds only what this widget instance put
        // there, and the host scopes it to the instance.
        if (!handlers.state) throw new HostError('host-error', 'state is not available on this host');
        return request.method === 'state.get' ? handlers.state.get() : handlers.state.set(params['value']);
      }
      case 'storage.get':
      case 'storage.set':
      case 'storage.remove': {
        const requested = params['contract'] as string;
        const contract = requested;
        const access = request.method === 'storage.get' ? 'read' : 'write';
        const granted = grants.storage?.[access] ?? [];
        if (!granted.includes(requested) && !granted.includes(contract)) denied(`storage:${access}:${contract}`);
        if (!handlers.storage) throw new HostError('host-error', 'persistent storage is not available on this host');
        if (request.method === 'storage.get') return handlers.storage.get(contract);
        const options = typeof params['ifRevision'] === 'number' ? { ifRevision: params['ifRevision'] as number } : undefined;
        if (request.method === 'storage.set') {
          return handlers.storage.set(contract, params['value'] as Record<string, unknown>, options);
        }
        return handlers.storage.remove(contract, options);
      }
      case 'functions.invoke': {
        const slug = params['slug'] as string;
        // Deny-by-default per slug, out of its own grant list: a project's
        // functions are not this SDK's operations, so an api grant never
        // authorises one and one granted slug never authorises another.
        if (!grants.functions?.includes(slug)) denied(`functions:${slug}`);
        if (!handlers.functions) {
          throw new HostError('host-error', 'backend functions are not available on this host');
        }
        return handlers.functions.invoke(slug, params['payload']);
      }
      case 'storage.catalog':
        if (!handlers.storage) throw new HostError('host-error', 'persistent storage is not available on this host');
        return handlers.storage.catalog();
      case 'ui.notify':
        return handlers.ui?.notify(params['notification'] as UiNotification);
      case 'ui.confirm':
        return handlers.ui ? handlers.ui.confirm(params['confirmation'] as UiConfirmation) : false;
      case 'events.emit':
        handlers.onEvent?.(params['name'] as string, params['payload']);
        return undefined;
      case 'resize':
        handlers.onResize?.(params['height'] as number);
        return undefined;
      default:
        throw new HostError('unknown-method', `unknown method: ${(request as RequestMessage).method}`);
    }
  }

  function onPortMessage(event: MessageEvent<PortMessage>): void {
    const msg = event.data;
    // Respond on the port the request arrived on: a host may open a fresh
    // channel per init attempt (e.g. re-sending init until the frame is ready),
    // and the widget keeps whichever port it received first.
    const source = event.target as MessagePort;
    if (msg.type === 'ready') {
      // Adopt the acknowledged port as canonical so stream pushes reach the
      // channel the widget actually kept.
      port = source;
      handlers.onReady?.();
      return;
    }
    if (msg.type !== 'request') return;
    dispatch(msg).then(
      (result) => source.postMessage({ type: 'response', id: msg.id, ok: true, result } satisfies ResponseMessage),
      (error: unknown) => {
        const code = error instanceof HostError ? error.code : 'host-error';
        const message = error instanceof Error ? error.message : String(error);
        const details = error instanceof HostError ? error.details : undefined;
        const serverCode = error instanceof HostError ? error.serverCode : undefined;
        source.postMessage({
          type: 'response',
          id: msg.id,
          ok: false,
          // Omitted rather than sent as undefined: the message is structured
          // cloned, and an absent key is what the client's helpers check for.
          error: { code, message, ...(details ? { details } : {}), ...(serverCode ? { serverCode } : {}) },
        } satisfies ResponseMessage);
      },
    );
  }

  const initMessage: InitMessage = { protocol: WIDGET_PROTOCOL, type: 'init', context, grants };

  return {
    initMessage,
    openPort(): MessagePort {
      const channel = new MessageChannel();
      port = channel.port1;
      port.onmessage = onPortMessage;
      return channel.port2;
    },
    initInto(target: Window, targetOrigin: string): void {
      const widgetPort = this.openPort();
      target.postMessage(initMessage, targetOrigin, [widgetPort]);
    },
    pushContext(stream: ContextStream, payload: unknown): void {
      port?.postMessage({ type: 'stream', stream, payload });
    },
    dispose(): void {
      subscriptions.forEach((teardown) => teardown());
      subscriptions.clear();
      port?.close();
      port = undefined;
    },
  };
}
